import type { WebPushEnableResultKind } from './services/WebPushService';

/**
 * PD-17322 — browser notifications while a tab of the app is open.
 *
 * Some browsers allow notifications but cannot subscribe to Web Push. Brave
 * ships with its push service switched off, so `pushManager.subscribe`
 * rejects even after the user allows notifications, and we will not ask
 * anyone to change browser settings. For those browsers the app shows each
 * new bell notification as a browser notification itself, from whichever of
 * its tabs is open (useOpenTabNotifier). Nothing reaches the browser once
 * every tab of the app is closed.
 *
 * Shared by the CRM and the storefront; each passes its own
 * `OpenTabAppModel`, so their keys, locks and notification tags never mix.
 * The owner is whoever is signed in: the storefront's public user id, or
 * the CRM's tenant alias and user id (the CRM serves every tenant from one
 * origin, and notification ids are per tenant).
 *
 * Two values per owner live in localStorage:
 * - `<prefix>on:<owner>`: open-tab mode is on in this browser. Keyed by the
 *   owner so a second person signing in on the same browser does not
 *   inherit it; both apps also clear their keys on sign-out.
 * - `<prefix>last_shown:<owner>`: the id of the newest notification already
 *   handled. Shared by every open tab, so only one tab shows a notification.
 *
 * The pure functions take the storage as a parameter so they can be tested.
 */

/** Where one app keeps its open-tab state. */
export interface OpenTabAppModel {
	/** localStorage key prefix; every key under it is that app's open-tab state. */
	storagePrefix: string;
	/** Web Lock name prefix, for the claim lock and the leader lock. */
	lockPrefix: string;
	/** Notification tag prefix; the notification id is appended. */
	tagPrefix: string;
}

/**
 * The CRM's open-tab state. Under `workd:`, so the CRM's sign-out sweep
 * (clearAllWorkdStorage) removes it with everything else.
 */
export const CRM_OPEN_TAB_APP: OpenTabAppModel = {
	storagePrefix: 'workd:crm_tab_notifications:',
	lockPrefix: 'workd-crm-tab-notifications',
	tagPrefix: 'crm-notification',
};

/**
 * The CRM's owner key: tenant alias and (global) user id. The CRM serves
 * every tenant from one origin and notification ids are per tenant, so the
 * user id alone would let one tenant's marker hide another tenant's
 * notifications for the same person. Null until both are known.
 */
export function crmOpenTabOwner(
	instanceAlias: string | null | undefined,
	userId: number | null | undefined,
): string | null {
	if (instanceAlias == null || instanceAlias === '' || userId == null) {
		return null;
	}
	return `${instanceAlias}:${userId}`;
}

/** How many of the newest notifications one check reads. */
export const OPEN_TAB_NOTIFICATIONS_FETCH_LIMIT = 10;

/**
 * At most this many browser notifications from one check, so a burst (or a
 * long-frozen tab waking up) cannot stack a screenful of them.
 */
export const OPEN_TAB_NOTIFICATIONS_MAX_SHOWN = 5;

/**
 * When a tab starts and this browser has been watching before, notifications
 * newer than the shared marker but at most this old are still shown. That
 * covers one that arrived while the page was loading or between the last
 * tab closing and this one opening, without replaying a backlog after a
 * long time away.
 */
export const OPEN_TAB_NOTIFICATIONS_CATCH_UP_MS = 10 * 60_000;

/** The slice of Storage these helpers use; `localStorage` satisfies it. */
export type OpenTabStorageModel = Pick<
	Storage,
	'getItem' | 'setItem' | 'removeItem' | 'key' | 'length'
>;

/** The slice of a bell row these helpers read. */
export interface OpenTabNotificationModel {
	id: number;
	isRead: boolean;
}

export function openTabModeStorageKey(
	app: OpenTabAppModel,
	owner: string,
): string {
	return `${app.storagePrefix}on:${owner}`;
}

export function lastShownStorageKey(
	app: OpenTabAppModel,
	owner: string,
): string {
	return `${app.storagePrefix}last_shown:${owner}`;
}

/** Whether open-tab mode is on for this owner in this browser. */
export function readOpenTabMode(
	storage: OpenTabStorageModel | null,
	app: OpenTabAppModel,
	owner: string,
): boolean {
	if (storage == null) {
		return false;
	}
	try {
		return storage.getItem(openTabModeStorageKey(app, owner)) === '1';
	} catch {
		return false;
	}
}

/**
 * Turn open-tab mode on or off. False when the storage refused the write:
 * without it no tab would know the mode is on, so the caller must not tell
 * the user it is. Turning it off also drops the last-shown marker, so the
 * next time it is turned on starts from that moment.
 */
export function writeOpenTabMode(
	storage: OpenTabStorageModel | null,
	app: OpenTabAppModel,
	owner: string,
	on: boolean,
): boolean {
	if (storage == null) {
		return !on;
	}
	try {
		if (on) {
			storage.setItem(openTabModeStorageKey(app, owner), '1');
		} else {
			storage.removeItem(openTabModeStorageKey(app, owner));
			storage.removeItem(lastShownStorageKey(app, owner));
		}
		return true;
	} catch {
		return false;
	}
}

/** Remove every open-tab key of one app, for every owner (sign-out). */
export function clearOpenTabNotificationsStorage(
	storage: OpenTabStorageModel | null,
	app: OpenTabAppModel,
): void {
	if (storage == null) {
		return;
	}
	try {
		const keys: Array<string> = [];
		for (let i = 0; i < storage.length; i++) {
			const key = storage.key(i);
			if (key != null && key.startsWith(app.storagePrefix)) {
				keys.push(key);
			}
		}
		for (const key of keys) {
			storage.removeItem(key);
		}
	} catch {
		// Storage unavailable: nothing was stored either.
	}
}

/** The stored last-shown id, or null when none is stored (or it is unreadable). */
export function readLastShownId(
	storage: OpenTabStorageModel | null,
	app: OpenTabAppModel,
	owner: string,
): number | null {
	if (storage == null) {
		return null;
	}
	try {
		const raw = storage.getItem(lastShownStorageKey(app, owner));
		if (raw == null) {
			return null;
		}
		const value = Number(raw);
		return Number.isSafeInteger(value) && value >= 0 ? value : null;
	} catch {
		return null;
	}
}

function writeLastShownId(
	storage: OpenTabStorageModel | null,
	app: OpenTabAppModel,
	owner: string,
	id: number,
): void {
	if (storage == null) {
		return;
	}
	try {
		storage.setItem(lastShownStorageKey(app, owner), String(id));
	} catch {
		// Not fatal: the browser's notification tag still collapses a repeat.
	}
}

function newestId(
	notifications: ReadonlyArray<OpenTabNotificationModel>,
): number {
	let max = 0;
	for (const notification of notifications) {
		if (notification.id > max) {
			max = notification.id;
		}
	}
	return max;
}

/**
 * The marker that counts everything on the bell as handled, so turning this
 * on never announces old notifications. Never moves backwards, so a tab that
 * read an older page cannot make another tab show something twice.
 */
export function seedLastShownId(
	stored: number | null,
	notifications: ReadonlyArray<OpenTabNotificationModel>,
): number {
	return Math.max(stored ?? 0, newestId(notifications));
}

/**
 * Which of the newest bell rows to show: unread and newer than the marker,
 * oldest first (so the newest lands on top), capped at `max` of the newest.
 * A row someone already read (on another device, say) is not shown.
 */
export function selectNotificationsToShow<T extends OpenTabNotificationModel>(
	notifications: ReadonlyArray<T>,
	lastShownId: number,
	max: number = OPEN_TAB_NOTIFICATIONS_MAX_SHOWN,
): Array<T> {
	const fresh = notifications
		.filter(
			(notification) => notification.id > lastShownId && !notification.isRead,
		)
		.sort((a, b) => a.id - b.id);
	return max > 0 ? fresh.slice(-max) : [];
}

/** Mark everything on the bell right now as handled. */
export function seedOpenTabMarker(
	storage: OpenTabStorageModel | null,
	app: OpenTabAppModel,
	owner: string,
	notifications: ReadonlyArray<OpenTabNotificationModel>,
): void {
	writeLastShownId(
		storage,
		app,
		owner,
		seedLastShownId(readLastShownId(storage, app, owner), notifications),
	);
}

/**
 * What a tab does when it starts watching. The first time (no marker yet)
 * everything on the bell counts as handled, so turning this on never
 * announces old notifications. After that the marker is kept, and rows newer
 * than it that arrived in the last few minutes are shown, so a notification
 * that landed while the page was loading is not lost. Run it inside
 * `withOpenTabNotificationsLock`, like `claimNotificationsToShow`.
 */
export function resumeOpenTabMarker<
	T extends OpenTabNotificationModel & { createdDate: string },
>(
	storage: OpenTabStorageModel | null,
	app: OpenTabAppModel,
	owner: string,
	notifications: ReadonlyArray<T>,
	now: number,
	max: number = OPEN_TAB_NOTIFICATIONS_MAX_SHOWN,
): Array<T> {
	const stored = readLastShownId(storage, app, owner);
	if (stored == null) {
		seedOpenTabMarker(storage, app, owner, notifications);
		return [];
	}
	const recent = notifications.filter((notification) => {
		const created = Date.parse(notification.createdDate);
		return (
			Number.isFinite(created) &&
			created >= now - OPEN_TAB_NOTIFICATIONS_CATCH_UP_MS
		);
	});
	const toShow = selectNotificationsToShow(recent, stored, max);
	const next = seedLastShownId(stored, notifications);
	if (next !== stored) {
		writeLastShownId(storage, app, owner, next);
	}
	return toShow;
}

/**
 * The cross-tab decision: which rows THIS tab shows. Reads the shared marker,
 * picks the rows newer than it, and moves the marker past everything on the
 * page in the same step, so the next tab to look finds nothing left to show.
 * Run it inside `withOpenTabNotificationsLock` so two tabs cannot read the
 * same marker at once.
 *
 * With no marker stored (another tab's sign-out or turn-off cleared it
 * mid-check) this seeds instead of showing, the same as a fresh start.
 */
export function claimNotificationsToShow<T extends OpenTabNotificationModel>(
	storage: OpenTabStorageModel | null,
	app: OpenTabAppModel,
	owner: string,
	notifications: ReadonlyArray<T>,
	max: number = OPEN_TAB_NOTIFICATIONS_MAX_SHOWN,
): Array<T> {
	const stored = readLastShownId(storage, app, owner);
	if (stored == null) {
		seedOpenTabMarker(storage, app, owner, notifications);
		return [];
	}
	const toShow = selectNotificationsToShow(notifications, stored, max);
	const next = seedLastShownId(stored, notifications);
	if (next !== stored) {
		writeLastShownId(storage, app, owner, next);
	}
	return toShow;
}

/**
 * What a settings page does after Turn on. Each app words the `off` reasons
 * itself.
 */
export type BrowserNotificationsTurnOnOutcomeModel =
	| { mode: 'push' }
	| { mode: 'open-tab' }
	| { mode: 'off'; reason: 'denied' | 'dismissed' | 'unavailable' };

/**
 * Map how the push subscribe ended to what the page does. Only
 * `push-unavailable` falls back to open-tab mode: notifications are allowed,
 * the browser just cannot receive push.
 */
export function browserNotificationsTurnOnOutcome(
	result: WebPushEnableResultKind,
): BrowserNotificationsTurnOnOutcomeModel {
	switch (result) {
		case 'subscribed':
			return { mode: 'push' };
		case 'push-unavailable':
			return { mode: 'open-tab' };
		case 'denied':
			return { mode: 'off', reason: 'denied' };
		case 'dismissed':
			return { mode: 'off', reason: 'dismissed' };
		case 'unsupported':
		case 'error':
		default:
			return { mode: 'off', reason: 'unavailable' };
	}
}

// ─────────────────────────────────────────────────────────────────
// Browser glue: the live localStorage, change notifications, Web Locks.
// ─────────────────────────────────────────────────────────────────

/** localStorage, or null where reading it throws (blocked site data). */
export function openTabStorage(): OpenTabStorageModel | null {
	try {
		return typeof localStorage === 'undefined' ? null : localStorage;
	} catch {
		return null;
	}
}

const localListeners = new Set<() => void>();

/**
 * Call `listener` when open-tab mode may have changed: in this tab (through
 * `setOpenTabMode`) or in another tab (the storage event, which only fires
 * in the OTHER tabs). Shaped for useSyncExternalStore once bound to an app.
 */
export function subscribeOpenTabMode(
	app: OpenTabAppModel,
	listener: () => void,
): () => void {
	localListeners.add(listener);
	const onStorage = (event: StorageEvent): void => {
		// A null key means the whole storage was cleared.
		if (event.key == null || event.key.startsWith(`${app.storagePrefix}on:`)) {
			listener();
		}
	};
	window.addEventListener('storage', onStorage);
	return (): void => {
		localListeners.delete(listener);
		window.removeEventListener('storage', onStorage);
	};
}

function notifyLocalListeners(): void {
	for (const listener of localListeners) {
		listener();
	}
}

export function isOpenTabModeOn(
	app: OpenTabAppModel,
	owner: string | null,
): boolean {
	return owner != null && readOpenTabMode(openTabStorage(), app, owner);
}

/** Turn open-tab mode on or off in this browser; false when it could not be stored. */
export function setOpenTabMode(
	app: OpenTabAppModel,
	owner: string,
	on: boolean,
): boolean {
	const stored = writeOpenTabMode(openTabStorage(), app, owner, on);
	notifyLocalListeners();
	return stored;
}

/** Sign-out: forget one app's open-tab mode for every owner on this browser. */
export function clearOpenTabNotifications(app: OpenTabAppModel): void {
	clearOpenTabNotificationsStorage(openTabStorage(), app);
	notifyLocalListeners();
}

/** The slice of the Web Locks API these helpers use; `navigator.locks` satisfies it. */
export type OpenTabLockManagerModel = Pick<LockManager, 'request'>;

/** navigator.locks, or null where the browser has no Web Locks API. */
export function openTabLockManager(): OpenTabLockManagerModel | null {
	if (typeof navigator === 'undefined') {
		return null;
	}
	return (navigator as { locks?: LockManager }).locks ?? null;
}

/**
 * Run `fn` while holding a lock shared by every tab of the app on this
 * origin (the Web Locks API), so two tabs cannot claim the same notification
 * at the same moment. Where the API is missing the claim runs unlocked; the
 * notification tag (one per notification id) still makes the browser replace
 * a duplicate rather than show it twice.
 */
export async function withOpenTabNotificationsLock<T>(
	app: OpenTabAppModel,
	owner: string,
	fn: () => T,
	locks: OpenTabLockManagerModel | null = openTabLockManager(),
): Promise<T> {
	if (locks == null) {
		return fn();
	}
	return locks.request(`${app.lockPrefix}-${owner}`, async () => fn());
}

/** Whether this tab is the one that reads the bell for open-tab notifications. */
export interface OpenTabLeadershipModel {
	isLeader: () => boolean;
	/** Give up leadership, or stop waiting for it. */
	release: () => void;
}

/**
 * One tab per owner reads the newest bell rows each minute; the others only
 * wait. Leadership is a Web Lock held for as long as the tab watches: the
 * first tab gets it at once, the rest queue behind it, and when the leader
 * closes (or turns the mode off) the browser hands the lock to the next tab,
 * which carries on from the shared marker.
 *
 * Without the Web Locks API every tab leads. Each still makes only the one
 * small request a minute, and the shared marker plus the notification tag
 * keep them from showing a notification twice.
 */
export function acquireOpenTabLeadership(
	app: OpenTabAppModel,
	owner: string,
	locks: OpenTabLockManagerModel | null = openTabLockManager(),
): OpenTabLeadershipModel {
	if (locks == null) {
		return { isLeader: () => true, release: () => undefined };
	}
	let leader = false;
	let released = false;
	let releaseHeld: (() => void) | null = null;
	const abort = new AbortController();
	locks
		.request(
			`${app.lockPrefix}-leader-${owner}`,
			{ signal: abort.signal },
			async () => {
				if (released) {
					return;
				}
				leader = true;
				// Held until release() resolves it.
				await new Promise<void>((resolve) => {
					releaseHeld = resolve;
				});
			},
		)
		.catch(() => {
			// Aborted while still queued: this tab stopped watching first.
		});
	return {
		isLeader: () => leader,
		release: (): void => {
			released = true;
			leader = false;
			abort.abort();
			releaseHeld?.();
		},
	};
}
